import React, { FC, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  TextField,
  Stack,
} from "@mui/material";
import { createSite, getRegion, updateSite } from "api";
import { useNotify } from "hooks";
import { QUERY_KEYS } from "consts";
import { COLORS, InputFieldWrapper, TYPOGRAPHY_COMMON_STYLES } from "components";
import { Controller, useForm } from "react-hook-form";
import dayjs from "dayjs";
import { formatDateToISOStartOfDay, getDateFormatset } from "utils";
import { AllowedFormValues } from "../types";

interface AddSiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  allowedValues: AllowedFormValues;
  editSite?: any;
  clickType?: string,
  editNotes?: boolean,
}

export const AddSiteModal: FC<AddSiteModalProps> = ({
  isOpen,
  onClose,
  allowedValues,
  editSite,
  clickType = "",
  editNotes = false,
}) => {
  const { countries, siteStatuses, siteTypes, countryRegions, siteAreaUnits } = allowedValues;
  const sortedCountries = countryRegions.sort((a, b) => a.localeCompare(b));
  const CountriesAccending = countries?.sort() || [];
  const siteTypesAccending = siteTypes?.sort() || [];

  const notify = useNotify();
  const queryClient = useQueryClient();

  const defaultValues: any = useMemo(() => {
    const endDate = clickType === "Enable"
      ? "2999-12-31T00:00:00Z"
      : clickType === ""
        ? editSite?.endDate || null
        : formatDateToISOStartOfDay(new Date());

    return {
      name: editSite?.name || null,
      address: editSite?.address || null,
      city: editSite?.city || null,
      zipCode: editSite?.zipCode || null,
      type: editSite?.type || null,
      status: editSite?.status || null,
      country: editSite?.country || null,
      countryRegion: editSite?.countryRegion || null,
      region: editSite?.region || null,
      customerSiteId: editSite?.customerSiteId || null,
      businessUnit: editSite?.businessUnit || null,
      startDate: editSite?.startDate || null,
      endDate,
      siteArea: editSite?.siteArea || null,
      siteAreaUnit: editSite?.siteAreaUnit || null,
      notes: editSite?.notes
    };
  }, [editSite, clickType]);

  const { control, reset, getValues, watch, setValue } = useForm<any>({
    mode: "onChange",
    defaultValues,
  });

  const [watchedCountryValue, watchedZipValue] = watch(['country', 'zipCode']);

  const [formValues, setFormValues] = useState<any>({ regionOptions: [] });
  const [loadingRegionCode, setLoadingRegionCode] = useState(false);

  async function fetchData(dataset: any) {
    setLoadingRegionCode(true);
    try {
      const dataResponse = await getRegion(dataset);
      const regionValue = dataResponse;

      let options: string[] = [];
      let valueToSet: any = '';

      if (Array.isArray(regionValue)) {
        options = regionValue;
        valueToSet = regionValue.length === 1 ? regionValue[0] : '';
      } else if (typeof regionValue === 'string' && regionValue) {
        if (regionValue !== 'MULTIPLE REGIONS FOUND') {
          options = [regionValue];
          valueToSet = regionValue;
        } else {
          options = [];
          valueToSet = '';
        }
      }

      setFormValues((prevValues: any) => ({
        ...prevValues,
        regionOptions: options,
      }));
      setValue('region', valueToSet);
    } catch (error) {
      console.error('Failed to fetch region data:', error);
      notify.error('Failed to fetch region data');
    } finally {
      setLoadingRegionCode(false);
    }
  }

  const handleAddSite = async () => {
    await createSiteRequest(getValues(), {
      onError: () => notify.error(`Failed to create site: ${getValues().name}!`),
      onSuccess: () => {
        notify.success(`Site successfully created: ${getValues().name}!`);
        queryClient.invalidateQueries([QUERY_KEYS.SITES]);
      },
    });
    reset(defaultValues);
    onClose();
  };

  const handleEditSite = async () => {
    const { id } = editSite;
    if (formValues.region === "MULTIPLE REGIONS FOUND") {
      notify.error("Please make sure to select an Electricity Region Code to calculate your emissions.");
    } else {
      const currentValues = getValues();

      const updatedSite = {
        ...formValues,
        id,
        name: currentValues.name === "" ? null : currentValues.name,
        address: currentValues.address === "" ? null : currentValues.address,
        city: currentValues.city === "" ? null : currentValues.city,
        zipCode: currentValues.zipCode === "" ? null : currentValues.zipCode,
        country: currentValues.country === "" ? null : currentValues.country,
        type: currentValues.type === "" ? null : currentValues.type,
        status: currentValues.status === "" ? null : currentValues.status,
        countryRegion: currentValues.countryRegion === "" ? null : currentValues.countryRegion,
        region: currentValues.region === "" ? null : currentValues.region,
        customerSiteId: currentValues?.customerSiteId === "" ? null : currentValues?.customerSiteId,
        businessUnit: currentValues?.businessUnit === "" ? null : currentValues?.businessUnit,
        startDate: currentValues?.startDate === "" ? null : currentValues?.startDate,
        endDate: currentValues?.endDate === "" ? null : currentValues?.endDate,
        siteArea: currentValues?.siteArea === "" ? null : currentValues?.siteArea,
        siteAreaUnit: currentValues?.siteAreaUnit === "" ? null : currentValues?.siteAreaUnit,
        notes: currentValues?.notes === "" ? null : currentValues?.notes
      };

      await updateSiteRequest(updatedSite, {
        onError: () => notify.error(`Failed to edit site: ${formValues.name}!`),
        onSuccess: () => {
          notify.success(`Site successfully edited: ${formValues.name}!`);
          queryClient.invalidateQueries([QUERY_KEYS.SITES]);
        },
      });
      setFormValues({});
      onClose();
    }
  };

  const handleCancel = () => {
    if (editSite) {
      onClose();
    } else {
      onClose();
      setFormValues({});
      reset(defaultValues);
    }
  }

  useEffect(() => {
    reset(defaultValues);
    const initialRegionOptions = Array.isArray(editSite?.region)
      ? (editSite?.region || [])
      : (editSite?.region ? [editSite.region] : []);
    setFormValues({ ...(editSite || {}), regionOptions: initialRegionOptions });
    if (editSite?.region !== undefined) {
      setValue('region', editSite.region ?? '');
    }
  }, [reset, defaultValues, editSite, setValue]);

  const { mutateAsync: createSiteRequest, isLoading } = useMutation(createSite);
  const { mutateAsync: updateSiteRequest, isLoading: updateLoading } = useMutation(updateSite);

  useEffect(() => {
    const getRegionCode = (zipCode: any, country: any) => {
      if (country !== null) {
        const dataset = { zipCode: zipCode !== undefined ? zipCode : null, country };
        fetchData(dataset);
      }
    };

    if (watchedCountryValue !== undefined) {
      getRegionCode(watchedZipValue, watchedCountryValue);
    }
  }, [watchedCountryValue, watchedZipValue, editSite]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>{editNotes ? "Site Notes" : editSite ? "Edit Site" : "Add New Site"}</DialogTitle>
      <DialogContent>
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack
            sx={{
              width: "100%",
              minWidth: { xs: "300px", sm: "360px", md: "400px" },
              gap: "1.5rem",
              marginTop: "7px",
            }}
          >
            {editNotes
              ? <Grid item xs={6}>
                <InputFieldWrapper label="Notes">
                  <Controller
                    name="notes"
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        sx={{ width: "100%" }}
                        helperText={error ? error.message : null}
                        size="small"
                        error={!!error}
                        onChange={onChange}
                        value={value}
                        multiline
                        rows={3}
                      />
                    )}
                  />
                </InputFieldWrapper>
              </Grid>
              : <Grid container spacing={2}>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Site Name">
                    <Controller
                      name="name"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Customer Site ID">
                    <Controller
                      name="customerSiteId"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Business Unit">
                    <Controller
                      name="businessUnit"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Street Address">
                    <Controller
                      name="address"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                      >
                        <InputFieldWrapper label="Start Date">
                          <DatePicker
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: {
                                  width: "100%",
                                }
                              },
                            }}
                            value={
                              field?.value
                                ? dayjs(field?.value).utc()
                                : null
                            }
                            onChange={(date) => field.onChange(dayjs(date).format('YYYY-MM-DDTHH:mm:ss[Z]'))}
                            format={getDateFormatset()}
                          />
                        </InputFieldWrapper>
                      </LocalizationProvider>
                    )
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                      >
                        <InputFieldWrapper label="End Date">
                          <DatePicker
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: {
                                  width: "100%",
                                  "& fieldset": {
                                    border: clickType === "Disable" ? "1.2px solid red" : "",
                                  },
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: clickType === "Disable" ? "red" : "", // remove hover effect if needed
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: clickType === "Disable" ? "red" : "", // remove focus effect if needed
                                    },
                                  },
                                }
                              },
                            }}
                            value={
                              field?.value
                                ? dayjs(field?.value).utc()
                                : null
                            }
                            onChange={(date) => field.onChange(dayjs(date).format('YYYY-MM-DDTHH:mm:ss[Z]'))}
                            format={getDateFormatset()}
                            minDate={clickType === "" ? null : clickType === "Enable" ? dayjs() : null}
                            maxDate={clickType === "" ? null : clickType === "Enable" ? null : dayjs()}
                          />
                        </InputFieldWrapper>
                      </LocalizationProvider>
                    )
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="City">
                    <Controller
                      name="city"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Zipcode">
                    <Controller
                      name="zipCode"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Type">
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="type-simple-select-label"
                          id="type-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {siteTypesAccending.map((t) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={t} value={t}>
                              {t}</MenuItem>
                          ))}
                        </Select>
                      )
                      }
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Status">
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="status-simple-select-label"
                          id="status-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {siteStatuses.map((t) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={t} value={t}>
                              {t}</MenuItem>
                          ))}
                        </Select>
                      )
                      }
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Country">
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="country-simple-select-label"
                          id="country-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {CountriesAccending.map((t) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={t} value={t}>
                              {t}</MenuItem>
                          ))}
                        </Select>
                      )
                      }
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Region">
                    <Controller
                      name="countryRegion"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="countryRegion-simple-select-label"
                          id="countryRegion-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {sortedCountries.map((country) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={country} value={country}>
                              {country.replaceAll("_", " ")}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Electricity Region Code">
                    <Controller
                      name="region"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="region-simple-select-label"
                          id="region-simple-select"
                          name="region"
                          aria-label="Electricity Region Code"
                          data-testid="region-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40, color: (formValues?.regionOptions?.length > 1) ? 'red' : 'inherit', }}
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value)}
                          disabled={(formValues?.regionOptions?.length || 0) <= 1}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',
                              },
                            },
                          }}
                        >
                          {(formValues?.regionOptions || []).map((option: string) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }}
                              key={option}
                              value={option}
                            >
                              {loadingRegionCode
                                ? <CircularProgress sx={{ color: COLORS.Romance, font: '18px', mx: '4px' }} size={16} />
                                : option
                              }
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Site Area">
                    <Controller
                      name="siteArea"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Site Area Unit">
                    <Controller
                      name="siteAreaUnit"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="siteAreaUnit-simple-select-label"
                          id="siteAreaUnit-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {siteAreaUnits?.map((unit) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={unit} value={unit}>
                              {unit}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
              </Grid>
            }
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={editSite ? handleEditSite : handleAddSite}
          variant="contained"
          color="primary"
          disabled={updateLoading || isLoading}
        >
          {updateLoading && <CircularProgress sx={{ color: COLORS.Romance, font: '18px', mx: '4px', position: 'absolute' }} size={20} />}
          {editSite ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
